import { useState, useMemo, useCallback } from "react";
import { cn } from "../../lib/cn";
import { useNavigate } from "react-router-dom";
import {
  useGanttTasks,
  useCrossProjectGanttTasks,
} from "../../lib/react-query/tasks";
import { useProjectMembers } from "../../lib/react-query/projects";
import { Spinner, CustomPopover } from "../shared";
import UpdateTaskModal from "./UpdateTaskModal";
import { useDebounce, toIsoDate, parseUsDate } from "../../utils/utils";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi2";
import { Filter } from "../../assets/icons";
import { Popover, PopoverTrigger, PopoverContent } from "@heroui/react";
import { DatePicker } from "@heroui/react";
import { parseDate } from "@internationalized/date";
import { I18nProvider } from "@react-aria/i18n";
import { motion, AnimatePresence } from "framer-motion";
import { getTaskColors } from "../../constants/taskColors";

const STATUS_OPTIONS = [
  { id: "PENDING", name: "Pending" },
  { id: "ACTIVE", name: "Active" },
  { id: "DONE", name: "Done" },
];

const PRIORITY_OPTIONS = [
  { id: "LOW", name: "Low" },
  { id: "MEDIUM", name: "Medium" },
  { id: "HIGH", name: "High" },
];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const getDaysLeftLabel = (task) => {
  const due = parseUsDate(task.due_date);
  if (!due) return "";
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Due today";
  if (diff > 0) return `${diff} day${diff > 1 ? "s" : ""} left`;
  const overdue = Math.abs(diff);
  return `${overdue} day${overdue > 1 ? "s" : ""} overdue`;
};

const getCalendarDays = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const days = [];

  for (let i = startOffset - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ date: d, isCurrentMonth: false });
  }

  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({ date: new Date(year, month, i), isCurrentMonth: true });
  }

  while (days.length % 7 !== 0) {
    const d = new Date(
      year,
      month + 1,
      days.length - startOffset - lastDay.getDate() + 1,
    );
    days.push({ date: d, isCurrentMonth: false });
  }

  return days;
};

const isTaskOnDay = (task, day) => {
  const s =
    task._start || parseUsDate(task.start_date) || parseUsDate(task.due_date);
  const e =
    task._end || parseUsDate(task.due_date) || parseUsDate(task.start_date);
  return s && e && day >= s && day <= e;
};

const getTaskDayRole = (task, day) => {
  const taskStart =
    task._start || parseUsDate(task.start_date) || parseUsDate(task.due_date);
  const taskEnd =
    task._end || parseUsDate(task.due_date) || parseUsDate(task.start_date);
  if (!taskStart || !taskEnd) return null;
  if (day < taskStart || day > taskEnd) return null;

  const isStart = day.getTime() === taskStart.getTime();
  const isEnd = day.getTime() === taskEnd.getTime();
  if (isStart && isEnd) return "single";
  if (isStart) return "start";
  if (isEnd) return "end";
  return "middle";
};

const GanttCalendarView = ({ projectId, crossProject = false }) => {
  const navigate = useNavigate();
  const today = new Date();
  const [viewDate, setViewDate] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1),
  );
  const [selectedTask, setSelectedTask] = useState(null);

  const [filters, setFilters] = useState({
    assignees: [],
    status: null,
    priority: null,
    search: "",
    dateStart: null,
    dateEnd: null,
  });
  const [openPopover, setOpenPopover] = useState(false);
  const [openFiltersPopover, setOpenFiltersPopover] = useState(false);
  const [openMoreDay, setOpenMoreDay] = useState(null);
  const [openMonthPicker, setOpenMonthPicker] = useState(false);
  const debouncedSearch = useDebounce(filters.search, 300);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const calendarDays = useMemo(
    () => getCalendarDays(year, month),
    [year, month],
  );

  const startDate = useMemo(
    () => toIsoDate(calendarDays[0].date),
    [calendarDays],
  );
  const endDate = useMemo(
    () => toIsoDate(calendarDays[calendarDays.length - 1].date),
    [calendarDays],
  );

  const params = useMemo(
    () => ({
      start: startDate,
      end: endDate,
      assignees:
        filters.assignees.length > 0 ? filters.assignees.join(",") : undefined,
      status: filters.status || undefined,
      priority: filters.priority || undefined,
      search: debouncedSearch || undefined,
    }),
    [
      startDate,
      endDate,
      filters.assignees,
      filters.status,
      filters.priority,
      debouncedSearch,
    ],
  );

  const { data: perProjectTasks, isLoading: loadingPerProject } = useGanttTasks(
    projectId,
    params,
    !crossProject && !!projectId,
  );
  const { data: crossProjectTasks, isLoading: loadingCrossProject } =
    useCrossProjectGanttTasks(params, crossProject);
  const tasks = crossProject ? crossProjectTasks : perProjectTasks;
  const isLoading = crossProject ? loadingCrossProject : loadingPerProject;
  const { data: members, isLoading: loadingMembers } = useProjectMembers(
    projectId,
    {},
    !crossProject && !!projectId,
  );

  // In cross-project mode, derive unique assignees from tasks data
  const derivedMembers = useMemo(() => {
    if (!crossProject) return members;
    if (!tasks || tasks.length === 0) return [];
    const seen = new Map();
    for (const task of tasks) {
      for (const assignee of task.assignees || []) {
        if (!seen.has(assignee.id)) {
          seen.set(assignee.id, {
            user: {
              id: assignee.id,
              email: assignee.email,
              full_name: assignee.name,
              profile_image_url: assignee.profile_image_url,
            },
          });
        }
      }
    }
    return Array.from(seen.values());
  }, [crossProject, members, tasks]);

  const weeks = useMemo(() => {
    const result = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      result.push(calendarDays.slice(i, i + 7));
    }
    return result;
  }, [calendarDays]);

  const parsedTasks = useMemo(() => {
    if (!tasks) return [];
    return tasks.map((t) => ({
      ...t,
      _start: parseUsDate(t.start_date) || parseUsDate(t.due_date),
      _end: parseUsDate(t.due_date) || parseUsDate(t.start_date),
    }));
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    return parsedTasks.filter((t) => {
      if (filters.dateStart) {
        const ds = parseUsDate(filters.dateStart);
        if (ds && t._end && t._end < ds) return false;
      }
      if (filters.dateEnd) {
        const de = parseUsDate(filters.dateEnd);
        if (de && t._start && t._start > de) return false;
      }
      return true;
    });
  }, [parsedTasks, filters.dateStart, filters.dateEnd]);

  const handlePrevMonth = useCallback(() => {
    setViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  }, []);

  const handleToday = useCallback(() => {
    const now = new Date();
    setViewDate(new Date(now.getFullYear(), now.getMonth(), 1));
  }, []);

  const toggleAssignee = useCallback((id) => {
    setFilters((f) => ({
      ...f,
      assignees: f.assignees.includes(id)
        ? f.assignees.filter((a) => a !== id)
        : [...f.assignees, id],
    }));
  }, []);

  const setStatus = useCallback((status) => {
    setFilters((f) => ({ ...f, status: f.status === status ? null : status }));
  }, []);

  const setPriority = useCallback((priority) => {
    setFilters((f) => ({
      ...f,
      priority: f.priority === priority ? null : priority,
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      assignees: [],
      status: null,
      priority: null,
      search: "",
      dateStart: null,
      dateEnd: null,
    });
  }, []);

  const today_ = new Date();
  const todayKey = `${today_.getFullYear()}-${today_.getMonth()}-${today_.getDate()}`;

  const hasActiveFilters =
    filters.assignees.length > 0 ||
    filters.status ||
    filters.priority ||
    filters.search ||
    filters.dateStart ||
    filters.dateEnd;
  const activeFilterCount =
    filters.assignees.length +
    (filters.status ? 1 : 0) +
    (filters.priority ? 1 : 0) +
    (filters.search ? 1 : 0) +
    (filters.dateStart ? 1 : 0) +
    (filters.dateEnd ? 1 : 0);

  return (
    <div className="flex min-w-0 flex-col gap-1 pb-6">
      {/* Header Section */}
      <div className={cn("flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 pt-4 mt-4 min-w-0")}>
        {/* Month Navigation */}
        <div className="flex w-full min-w-0 items-center gap-1 sm:gap-2 bg-white rounded-xl border border-surface-border p-1 shadow-sm lg:w-fit">
          <button
            onClick={handlePrevMonth}
            className="shrink-0 p-2 rounded-lg hover:bg-surface-hover transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-secondary/30"
            aria-label="Previous month"
          >
            <HiChevronLeft className="w-5 h-5 text-darkgrey" />
          </button>
          <Popover
            isOpen={openMonthPicker}
            onOpenChange={setOpenMonthPicker}
            placement="bottom"
            offset={8}
            classNames={{
              base: "shadow-md !rounded-md",
              content: "!p-0 !rounded-md",
            }}
          >
            <PopoverTrigger>
              <h2 className="min-w-0 flex-1 text-center text-lg sm:text-xl font-bold text-primary lg:min-w-[160px] font-sans tracking-tight cursor-pointer hover:bg-surface rounded-lg px-2 sm:px-3 py-1 transition-colors whitespace-nowrap">
                {MONTH_NAMES[month]} {year}
              </h2>
            </PopoverTrigger>
            <PopoverContent className="w-full">
              <I18nProvider locale="en-US">
                <div className="p-3 bg-white rounded-md w-64">
                  <div className="flex items-center justify-between mb-3">
                    <button
                      onClick={() =>
                        setViewDate(
                          (d) => new Date(d.getFullYear() - 1, d.getMonth(), 1),
                        )
                      }
                      className="p-1 rounded hover:bg-surface-hover"
                      aria-label="Previous year"
                    >
                      <HiChevronLeft className="w-4 h-4 text-darkgrey" />
                    </button>
                    <span className="text-sm font-semibold text-primary">
                      {year}
                    </span>
                    <button
                      onClick={() =>
                        setViewDate(
                          (d) => new Date(d.getFullYear() + 1, d.getMonth(), 1),
                        )
                      }
                      className="p-1 rounded hover:bg-surface-hover"
                      aria-label="Next year"
                    >
                      <HiChevronRight className="w-4 h-4 text-darkgrey" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {MONTH_NAMES.map((mName, idx) => (
                      <button
                        key={mName}
                        onClick={() => {
                          setViewDate(new Date(year, idx, 1));
                          setOpenMonthPicker(false);
                        }}
                        className={`px-2 py-2 text-xs font-medium rounded-lg transition-colors ${
                          idx === month
                            ? "bg-secondary text-white shadow-sm"
                            : "bg-surface-hover text-grey hover:bg-surface-active"
                        }`}
                      >
                        {mName.slice(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>
              </I18nProvider>
            </PopoverContent>
          </Popover>
          <button
            onClick={handleNextMonth}
            className="shrink-0 p-2 rounded-lg hover:bg-surface-hover transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-secondary/30"
            aria-label="Next month"
          >
            <HiChevronRight className="w-5 h-5 text-darkgrey" />
          </button>
          <div className="hidden sm:block w-px h-6 bg-surface-active mx-1" />
          <button
            onClick={handleToday}
            className="shrink-0 px-3 sm:px-4 py-2 text-sm font-semibold text-secondary-dark hover:bg-secondary/10 rounded-lg transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-secondary/30"
          >
            Today
          </button>
        </div>

        {/* Filter Bar */}
        <div className="flex w-full min-w-0 flex-col items-stretch lg:items-end justify-end gap-1">
          <div className="grid w-full min-w-0 grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end sm:gap-3">
            {/* Search */}
            <div className="relative col-span-2 sm:col-span-1 sm:w-56">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-mediumgrey"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search tasks..."
                value={filters.search}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, search: e.target.value }))
                }
                className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-surface-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary transition-all placeholder:text-mediumgrey"
              />
            </div>
            {/* Filters Dropdown */}
            <CustomPopover
              open={openFiltersPopover}
              setOpen={setOpenFiltersPopover}
              placement="bottom-end"
              trigger={
                <button
                  className={`flex w-full items-center justify-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-secondary/30 ${
                    filters.status ||
                    filters.priority ||
                    filters.dateStart ||
                    filters.dateEnd
                      ? "bg-secondary text-white shadow-sm"
                      : "bg-white border border-surface-border text-grey hover:bg-surface"
                  }`}
                  aria-expanded={openFiltersPopover}
                >
                  <Filter className="w-4 h-4" />
                  Filters
                  {(filters.status ||
                    filters.priority ||
                    filters.dateStart ||
                    filters.dateEnd) && (
                    <span className="ml-1 w-5 h-5 text-xs bg-white/20 text-white rounded-full flex items-center justify-center">
                      {(filters.status ? 1 : 0) +
                        (filters.priority ? 1 : 0) +
                        (filters.dateStart ? 1 : 0) +
                        (filters.dateEnd ? 1 : 0)}
                    </span>
                  )}
                </button>
              }
            >
              <div className="w-72 max-h-[60vh] overflow-y-auto custom-scrollbar bg-white p-4 rounded-xl shadow-lg border border-surface-border">
                <div className="space-y-4">
                  {/* Status Filter */}
                  <div className="">
                    <p className="text-xs font-semibold text-mediumgrey uppercase tracking-wider mb-2">
                      Status
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {STATUS_OPTIONS.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => setStatus(s.id)}
                          aria-pressed={filters.status === s.id}
                          className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-secondary/30 ${
                            filters.status === s.id
                              ? "bg-secondary text-white shadow-sm"
                              : "bg-surface-hover text-darkgrey hover:bg-surface-active"
                          }`}
                        >
                          {s.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Priority Filter */}
                  <div>
                    <p className="text-xs font-semibold text-mediumgrey uppercase tracking-wider mb-2">
                      Priority
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {PRIORITY_OPTIONS.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => setPriority(p.id)}
                          aria-pressed={filters.priority === p.id}
                          className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                            filters.priority === p.id
                              ? "bg-primary text-white shadow-sm"
                              : "bg-surface-hover text-darkgrey hover:bg-surface-active"
                          }`}
                        >
                          {p.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Date Range Filter */}
                  <div>
                    <p className="text-xs font-semibold text-mediumgrey uppercase tracking-wider mb-2">
                      Date Range
                    </p>
                    <I18nProvider locale="en-US">
                      <div className="flex flex-col gap-2">
                        <DatePicker
                          label="Start Date"
                          disableAnimation
                          value={
                            filters.dateStart
                              ? parseDate(filters.dateStart)
                              : null
                          }
                          onChange={(date) =>
                            setFilters((f) => ({
                              ...f,
                              dateStart: date
                                ? `${String(date.month).padStart(2, "0")}/${String(date.day).padStart(2, "0")}/${date.year}`
                                : null,
                            }))
                          }
                          showMonthAndYearPickers
                          classNames={{
                            base: "w-full",
                            inputWrapper:
                              "h-9 shadow-none rounded-lg border border-surface-border",
                            label: "text-[10px] text-mediumgrey",
                          }}
                        />
                        <DatePicker
                          label="End Date"
                          disableAnimation
                          value={
                            filters.dateEnd ? parseDate(filters.dateEnd) : null
                          }
                          onChange={(date) =>
                            setFilters((f) => ({
                              ...f,
                              dateEnd: date
                                ? `${String(date.month).padStart(2, "0")}/${String(date.day).padStart(2, "0")}/${date.year}`
                                : null,
                            }))
                          }
                          showMonthAndYearPickers
                          classNames={{
                            base: "w-full",
                            inputWrapper:
                              "h-9 shadow-none rounded-lg border border-surface-border",
                            label: "text-[10px] text-mediumgrey",
                          }}
                        />
                      </div>
                    </I18nProvider>
                  </div>
                </div>
              </div>
            </CustomPopover>
            {/* Assignees Dropdown + Clear */}
            <div className="flex flex-col gap-1.5">
              <CustomPopover
                open={openPopover}
                setOpen={setOpenPopover}
                placement="bottom-end"
                trigger={
                  <button
                    className={`flex w-full items-center justify-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-secondary/30 ${
                      filters.assignees.length > 0
                        ? "bg-secondary text-white shadow-sm"
                        : "bg-white border border-surface-border text-grey hover:bg-surface"
                    }`}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                    Assignees
                    {filters.assignees.length > 0 && (
                      <span className="ml-1 w-5 h-5 text-xs bg-white/20 text-white rounded-full flex items-center justify-center">
                        {filters.assignees.length}
                      </span>
                    )}
                  </button>
                }
              >
                <div className="w-72 max-h-[60vh] overflow-y-auto custom-scrollbar bg-white p-4 rounded-xl shadow-lg border border-surface-border">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-sm text-primary">
                      Filter by Assignee
                    </p>
                  </div>
                  {loadingMembers && !crossProject ? (
                    <div className="py-8 center">
                      <Spinner size="sm" />
                    </div>
                  ) : (
                    <div className="max-h-56 overflow-y-auto space-y-1">
                      {(derivedMembers || []).map((m) => {
                        const user = m?.user;
                        const id = String(user?.id ?? m.user);
                        const name =
                          user?.full_name ||
                          user?.name ||
                          user?.email ||
                          `User ${id}`;
                        const initials =
                          name
                            .split(" ")
                            .map((w) => w[0])
                            .join("")
                            .slice(0, 2)
                            .toUpperCase() || "U";
                        const isSelected = filters.assignees.includes(id);

                        return (
                          <button
                            key={id}
                            onClick={() => toggleAssignee(id)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 cursor-pointer ${
                              isSelected
                                ? "bg-secondary/10 ring-1 ring-secondary/30"
                                : "hover:bg-surface"
                            }`}
                          >
                            <div className="w-9 h-9 rounded-full bg-secondary text-white text-xs font-medium flex items-center justify-center border-2 border-white shrink-0">
                              {initials}
                            </div>
                            <span className="text-sm text-grey flex-1 text-left truncate font-medium">
                              {name}
                            </span>
                            {isSelected && (
                              <svg
                                className="w-5 h-5 text-secondary-dark"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </button>
                        );
                      })}
                      {!derivedMembers?.length && (
                        <p className="text-sm text-mediumgrey text-center py-4">
                          No members found
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </CustomPopover>
            </div>
          </div>

          {/* Clear All */}
          <div className="h-8 w-full flex items-center justify-end">
            <AnimatePresence initial={false}>
              {hasActiveFilters && (
                <motion.button
                  initial={{ opacity: 0, y: -8, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.96 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                  onClick={clearFilters}
                  className="flex w- flex-row items-center justify-center gap-1.5 text-xs font-medium text-mediumgrey hover:text-grey  rounded-lg transition-colors duration-200 cursor-pointer"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  Clear {activeFilterCount > 0 && `(${activeFilterCount})`}
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="py-16 center">
          <div className="flex flex-col items-center gap-3">
            <Spinner size="lg" />
            <p className="text-sm text-mediumgrey">Loading tasks...</p>
          </div>
        </div>
      ) : (
        <div className="min-w-0 border border-surface-border rounded-2xl overflow-hidden bg-white shadow-sm">
          {/* Day Headers */}
          <div className="grid grid-cols-7 border-b border-surface-border bg-surface/80">
            {DAY_NAMES.map((day, idx) => (
              <div
                key={day}
                className={`px-3 py-3 text-center text-xs font-bold text-mediumgrey uppercase tracking-wider ${
                  idx >= 5 ? "bg-surface-hover/50" : ""
                }`}
              >
                <span className="hidden sm:inline">{day}</span>
                <span className="sm:hidden">{day.charAt(0)}</span>
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="divide-y divide-surface-hover">
            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} className="grid grid-cols-7">
                {week.map((day, dayIndex) => {
                  const dayKey = `${day.date.getFullYear()}-${day.date.getMonth()}-${day.date.getDate()}`;
                  const isToday = dayKey === todayKey;
                  const isWeekend = dayIndex >= 5;

                  const dayTasks = filteredTasks
                    .filter((t) => isTaskOnDay(t, day.date))
                    .map((t) => ({
                      task: t,
                      role: getTaskDayRole(t, day.date),
                    }))
                    .filter((t) => t.role);

                  const visibleTasks = dayTasks.slice(0, 4);
                  const hiddenCount = dayTasks.length - visibleTasks.length;

                  return (
                    <div
                      key={dayIndex}
                      className={`relative border-r border-surface-border p-2 min-h-[140px] transition-colors duration-200 flex flex-col ${
                        day.isCurrentMonth
                          ? isWeekend
                            ? "bg-surface/30"
                            : "bg-white"
                          : "bg-surface/60"
                      } ${isToday ? "ring-1 ring-inset ring-secondary/30 bg-secondary/5" : ""} ${
                        dayIndex === 6 ? "border-r-0" : ""
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        {dayTasks.length > 0 && (
                          <span className="text-[10px] font-medium text-mediumgrey">
                            {dayTasks.length}
                          </span>
                        )}
                        <span
                          className={`text-sm font-semibold w-7 h-7 flex items-center justify-center rounded-full transition-all ${
                            isToday
                              ? "bg-secondary text-white shadow-md shadow-secondary/30"
                              : day.isCurrentMonth
                                ? isWeekend
                                  ? "text-mediumgrey"
                                  : "text-grey"
                                : "text-surface-disabled"
                          }`}
                        >
                          {day.date.getDate()}
                        </span>
                      </div>

                      {/* Task thread items */}
                      <div className="flex flex-col gap-1">
                        {visibleTasks.map(({ task, role }) => {
                          const taskColor = task.color || "#38C6FF";
                          const colors = getTaskColors(taskColor);
                          const dateLabel = getDaysLeftLabel(task);
                          const borderRadius =
                            role === "single"
                              ? "4px"
                              : role === "start"
                                ? "4px 0 0 4px"
                                : role === "end"
                                  ? "0 4px 4px 0"
                                  : "0";

                          return (
                            <button
                              key={task.id}
                              onClick={() =>
                                setSelectedTask({
                                  projectId: crossProject
                                    ? task.project
                                    : projectId,
                                  taskId: task.id,
                                  task,
                                })
                              }
                              className="w-full text-left px-1 py-0.5 text-[10px] font-medium hover:opacity-90 cursor-pointer focus:outline-none focus:ring-1 focus:ring-offset-0 focus:ring-secondary/50"
                              style={{
                                backgroundColor: colors.bg,
                                color: colors.text,
                                boxShadow: `0 1px 2px ${colors.shadow}`,
                                borderRadius,
                              }}
                              title={task.title}
                            >
                              <span className="flex flex-col items-start gap-0">
                                <span className="truncate w-full">
                                  {task.title}
                                </span>
                                {dateLabel && (
                                  <span className="opacity-80 text-[9px] font-normal">
                                    {dateLabel}
                                  </span>
                                )}
                              </span>
                            </button>
                          );
                        })}
                        {hiddenCount > 0 && (
                          <Popover
                            isOpen={openMoreDay === dayKey}
                            onOpenChange={(isOpen) =>
                              setOpenMoreDay(isOpen ? dayKey : null)
                            }
                            placement="bottom"
                            offset={8}
                            classNames={{
                              base: "shadow-md !rounded-md",
                              content: "!p-0 !rounded-md",
                            }}
                          >
                            <PopoverTrigger>
                              <button
                                type="button"
                                className="text-[10px] text-mediumgrey pl-0.5 hover:text-darkgrey text-left cursor-pointer"
                              >
                                +{hiddenCount} more
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-full">
                              <div className="p-2 min-w-[200px] max-w-[280px] max-h-[300px] overflow-y-auto">
                                <p className="text-xs font-semibold text-grey mb-2">
                                  {hiddenCount} more task
                                  {hiddenCount === 1 ? "" : "s"}
                                </p>
                                <div className="flex flex-col gap-1">
                                  {dayTasks.slice(4).map(({ task, role }) => {
                                    const taskColor = task.color || "#38C6FF";
                                    const colors = getTaskColors(taskColor);
                                    const dateLabel = getDaysLeftLabel(task);
                                    const borderRadius =
                                      role === "single"
                                        ? "4px"
                                        : role === "start"
                                          ? "4px 0 0 4px"
                                          : role === "end"
                                            ? "0 4px 4px 0"
                                            : "0";
                                    return (
                                      <button
                                        key={task.id}
                                        type="button"
                                        onClick={() => {
                                          setOpenMoreDay(null);
                                          setSelectedTask({
                                            projectId: crossProject
                                              ? task.project
                                              : projectId,
                                            taskId: task.id,
                                            task,
                                          });
                                        }}
                                        className="w-full text-left px-1 py-0.5 text-[10px] font-medium hover:opacity-90 cursor-pointer focus:outline-none focus:ring-1 focus:ring-offset-0 focus:ring-secondary/50"
                                        style={{
                                          backgroundColor: colors.bg,
                                          color: colors.text,
                                          boxShadow: `0 1px 2px ${colors.shadow}`,
                                          borderRadius,
                                        }}
                                        title={task.title}
                                      >
                                        <span className="flex flex-col items-start gap-0">
                                          <span className="truncate w-full">
                                            {task.title}
                                          </span>
                                          {dateLabel && (
                                            <span className="opacity-80 text-[9px] font-normal">
                                              {dateLabel}
                                            </span>
                                          )}
                                        </span>
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && (!filteredTasks || filteredTasks.length === 0) && (
        <div className="py-16 px-4">
          <div className="max-w-md mx-auto text-center">
            <div className="w-16 h-16 bg-surface-hover rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-mediumgrey"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-primary mb-2">
              {hasActiveFilters
                ? "No tasks match your filters"
                : "No tasks scheduled"}
            </h3>
            <p className="text-sm text-mediumgrey mb-4">
              {hasActiveFilters
                ? "Try adjusting your filters to see more tasks."
                : "Get started by creating your first task for this period."}
            </p>
            {hasActiveFilters ? (
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm font-medium text-secondary-dark hover:bg-secondary/10 rounded-lg transition-colors"
              >
                Clear filters
              </button>
            ) : !crossProject ? (
              <button
                onClick={() => navigate(`/projects/${projectId}/tasks`)}
                className="px-4 py-2 text-sm font-medium text-white bg-secondary hover:bg-secondary/90 rounded-lg transition-colors shadow-sm"
              >
                Create task
              </button>
            ) : null}
          </div>
        </div>
      )}

      {/* Task Detail Modal */}
      {selectedTask && (
        <UpdateTaskModal
          isOpen={true}
          onClose={() => setSelectedTask(null)}
          projectId={selectedTask.projectId}
          taskId={selectedTask.taskId}
          initialTask={selectedTask.task}
        />
      )}
    </div>
  );
};

export default GanttCalendarView;
